/**
 * Mic cleanup chain (WebRTC / Voice + Performance council) — a Web Audio graph that sits BETWEEN the raw
 * getUserMedia track and what we produce to mediasoup, so peers hear ~speech-only signal instead of
 * keystrokes + room tone.
 *
 * Why this exists (the player's pain): in 10+ duck sessions, open mics flood the channel with
 * keyboard clatter and background hum, drowning the one person actually talking. The browser's own
 * noiseSuppression/echoCancellation help, but they're tuned to be transparent and still let a lot of
 * impulsive keystroke energy + steady room tone through. We stack a deterministic DSP chain on top:
 *
 *   source → high-pass (kill rumble/AC hum/desk thumps) → low-pass (tame harsh key-click transients)
 *          → NOISE GATE (mute everything below a speech threshold — silences keys + room tone
 *            BETWEEN words, the single biggest win) → destination (the track we actually produce).
 *
 * The gate is RMS-driven from a detector analyser placed BEFORE the gate gain (so it can SEE speech
 * onset and open — a post-gate detector would deadlock shut). A SEPARATE analyser also taps the
 * filtered PRE-gate signal so the player's own level meter + speaking ring reflect THEIR live (filtered)
 * mic robustly — never coupled to the gate's open/closed state. (A post-gate self-tap deadlocks the
 * self-ring shut on borderline-level speech: the gate suppresses exactly what the self-VAD then fails to
 * see. What PEERS hear stays honest via the gated produced track + the server's active-speaker set.)
 *
 * Server-authoritative voice, mute/PTT, the device picker, and the re-handshake all stay correct
 * because this module ONLY transforms the audio: {@link VoiceClient} still owns the producer, the
 * mute gate (track.enabled + producer pause), and lifecycle. The processed output track's `enabled`
 * is still flipped by mute, and the whole graph is torn down deterministically via {@link destroy}.
 *
 * The GATE is now a VAD AudioWorklet (ADR-0044): it opens on energy + VOICING (periodicity) on the audio
 * thread, so loud NON-speech (fans, keyboard, a TV) no longer opens the channel and gets forwarded to the
 * room. The legacy main-thread RMS gate (below) remains as a fallback when AudioWorklet is unavailable.
 */
import { DEFAULT_VAD_PARAMS } from '@duck/contracts';
import { createVadGateNode, type VadGateOptions } from './vad-worklet.js';

/** Tunables for the cleanup chain. Defaults target keystrokes + steady room noise on a typical laptop. */
export interface MicPipelineOptions {
  /**
   * High-pass corner (Hz). Cuts sub-speech rumble: HVAC hum (~50–60 Hz + harmonics), desk/keyboard
   * thumps, mic-stand bumps, plosive thud — none of which carry intelligibility. 90 Hz keeps the full
   * vocal fundamental range (male voice bottoms out ~85 Hz) while removing the floor it sits on.
   */
  highPassHz: number;
  /**
   * Low-pass corner (Hz). Keystrokes are broadband CLICKS with strong high-frequency transient energy;
   * speech intelligibility lives mostly under ~8 kHz. A gentle roll-off at 12 kHz shaves the harshest
   * click "tick" without dulling the voice — kept high so it never sounds muffled (quality first).
   */
  lowPassHz: number;
  /**
   * Gate OPEN threshold (RMS, 0..1). Speech onset must exceed this to un-mute the channel. Set above a
   * quiet room floor + isolated key taps but below conversational speech, so typing while NOT talking
   * stays gated. Hysteresis (see closeThreshold) prevents chatter on word boundaries.
   *
   * INVARIANT — gate-open ≤ speaking-floor: this MUST sit at or below the self-VAD "you are speaking /
   * transmitting" start floor (voice-client self-VAD: rms > 0.02). If the gate opened ABOVE that floor,
   * a genuinely-speaking-but-quiet talker (rms ~0.02–0.025) would register as speaking yet be held shut
   * by the gate → their voice is muted entirely (not just quieted) — soft talkers vanish in a 10+
   * session. Keeping gate-open ≤ VAD floor guarantees "registered as speaking" ⇒ "audible". Do NOT raise
   * this above ~0.02 without lowering the self-VAD floor in lockstep.
   */
  openThreshold: number;
  /**
   * Gate CLOSE threshold (RMS, 0..1) — lower than open (hysteresis). Once talking, we keep the gate
   * open through brief dips (between words / soft syllables) so speech never choppily cuts in and out;
   * the gate only closes when the level falls below this quieter level.
   */
  closeThreshold: number;
  /** Gate attack time-constant (s) — how fast the gate OPENS. Fast so we never clip a word's onset. */
  attack: number;
  /** Gate release time-constant (s) — how slow the gate CLOSES. Slow-ish so tails/quiet ends survive. */
  release: number;
  /** Hold (ms) — after the level drops below close, keep the gate open this long before releasing, so
   *  natural pauses between words don't slam it shut (the most audible "robotic gate" artifact). */
  holdMs: number;
  /** Detector poll interval (ms). Fast enough to catch onsets, cheap enough for 10+ peers in a tab. */
  pollMs: number;
}

export const DEFAULT_MIC_PIPELINE_OPTIONS: MicPipelineOptions = {
  highPassHz: 90,
  lowPassHz: 12_000,
  // Energy floors + hold are SHARED with the VAD gate (DEFAULT_VAD_PARAMS) so the worklet path and this
  // legacy-fallback path can never drift to different gate levels — one source of truth (ADR-0044 review).
  // 0.015 sits under the self-VAD speaking floor (rms > 0.02) so soft talkers pass; keystroke taps + steady
  // room tone are sub-~0.01 RMS (and the high-pass strips their rumble), so keys/hum stay gated.
  openThreshold: DEFAULT_VAD_PARAMS.openThreshold,
  closeThreshold: DEFAULT_VAD_PARAMS.closeThreshold,
  holdMs: DEFAULT_VAD_PARAMS.holdMs, // keep open past the last speech so inter-word gaps don't slam the gate
  attack: 0.01, // 10 ms — effectively instant on a word onset (no clipped consonants)
  release: 0.2, // 200 ms — musical tail; avoids choppy cut-outs the brief warns against
  pollMs: 30, // ~33 Hz detector — snappy onset detection, negligible CPU
};

/** Live gate state the decision reads + advances. */
export interface GateState {
  /** Is the gate currently open (passing audio)? */
  open: boolean;
  /** Wall-clock (ms) until which an open gate is held before it may release. */
  holdUntil: number;
}

/**
 * PURE noise-gate transition decision (no Web Audio — unit-testable). Given the current {@link GateState},
 * this tick's filtered-signal `rms`, the wall-clock `wall`, and the `gap` (ms) since the previous tick,
 * returns the next state + whether to ramp the gate `open`/`close` (or `null` = leave the gain as-is).
 *
 * The critical rule: the detector runs on a main-thread `setInterval`, so under render/sound load it can
 * STARVE. A starved tick (a `gap` far larger than the poll interval) is a single, untrustworthy snapshot
 * of a long unobserved stretch — it must NEVER close a live gate, or one stall landing in an inter-word
 * dip severs the mic (with Opus DTX, a full cutoff). So on a starved tick we extend the hold and defer the
 * close to a healthy tick. Opening is unguarded — a starved tick that sees speech still opens (we always
 * bias toward transmitting). This is the fix for the "speaker's voice fully cuts out under load" bug.
 */
export function decideGate(
  state: GateState,
  rms: number,
  wall: number,
  gap: number,
  opts: MicPipelineOptions,
): { open: boolean; holdUntil: number; ramp: 'open' | 'close' | null } {
  // A healthy tick gap is ~pollMs; treat anything well past it (and past most of a hold) as starvation.
  const starved = gap > Math.max(opts.pollMs * 3, opts.holdMs * 0.6);
  if (state.open) {
    if (rms >= opts.closeThreshold) return { open: true, holdUntil: wall + opts.holdMs, ramp: null };
    if (wall >= state.holdUntil) {
      // Quiet past the hold. Close ONLY on a healthy tick — never sever a live mic on a starved snapshot.
      if (starved) return { open: true, holdUntil: wall + opts.holdMs, ramp: null };
      return { open: false, holdUntil: state.holdUntil, ramp: 'close' };
    }
    return { open: true, holdUntil: state.holdUntil, ramp: null };
  }
  // Closed: open on speech onset (unguarded — even a starved tick opens, so we never clip a word's start).
  if (rms >= opts.openThreshold) return { open: true, holdUntil: wall + opts.holdMs, ramp: 'open' };
  return { open: false, holdUntil: state.holdUntil, ramp: null };
}

/**
 * One built mic cleanup chain. Prefer the async {@link MicPipeline.create} factory — it loads the VAD
 * AudioWorklet (ADR-0044) that gates on VOICED speech on the audio thread; if the worklet is unavailable
 * it transparently falls back to the legacy main-thread RMS gate (this same class, `vadNode = null`).
 * Read {@link outputTrack} as the track to produce, and {@link levelAnalyser} for the HUD meter / self-VAD.
 * Call {@link destroy} on stop/reconnect/device-swap to release every node (no leaked graph between
 * re-handshakes — the brief's M3.5b requirement).
 */
export class MicPipeline {
  /**
   * Build the pipeline with the VAD AudioWorklet gate when available (the default + recommended path,
   * ADR-0044): it gates on energy + VOICING on the audio thread, so loud NON-speech (fans, keyboards, a
   * TV) no longer opens the channel. Falls back to the legacy RMS gate when AudioWorklet can't load, so
   * voice always works. Async only because `audioWorklet.addModule` is.
   */
  static async create(
    ctx: AudioContext,
    rawStream: MediaStream,
    options?: Partial<MicPipelineOptions> & { vad?: Partial<VadGateOptions> },
  ): Promise<MicPipeline> {
    const vadNode = await createVadGateNode(ctx, options?.vad).catch(() => null);
    return new MicPipeline(ctx, rawStream, vadNode, options);
  }

  /** The cleaned mic track to hand to mediasoup `produce()` / `replaceTrack()`. */
  readonly outputTrack: MediaStreamTrack;
  /** Self meter / speaking-ring analyser: taps the FILTERED but PRE-gate signal so the player's own level
   *  meter + speaking ring reflect THEIR live (rumble/key-reduced) mic robustly — decoupled from the gate
   *  (a post-gate self-tap deadlocks the self-ring shut on borderline speech). What peers hear stays
   *  honest via the gated produced track + the server active-speaker set, not this local tap. */
  readonly levelAnalyser: AnalyserNode;
  /** True while the VAD worklet reports the gate OPEN (voiced speech). False in legacy mode. For the HUD. */
  speaking = false;
  /** Latest VAD telemetry (ADR-0044) for the HUD + diagnostics: rms + voicing of the most recent frame. */
  lastRms = 0;
  lastVoicing = 0;
  /** True when the VAD AudioWorklet gate is active (vs the legacy RMS-gate fallback). */
  get usingVad(): boolean {
    return this.vadNode !== null;
  }

  private readonly ctx: AudioContext;
  private readonly opts: MicPipelineOptions;

  private readonly source: MediaStreamAudioSourceNode;
  private readonly highPass: BiquadFilterNode;
  private readonly lowPass: BiquadFilterNode;
  private readonly destination: MediaStreamAudioDestinationNode;

  /** PRIMARY gate (ADR-0044): the VAD AudioWorklet (audio thread). Null ⇒ legacy RMS gate is used. */
  private readonly vadNode: AudioWorkletNode | null;

  // ── Legacy main-thread RMS gate (used only when the worklet is unavailable) ──────────────────────
  private readonly detectorAnalyser?: AnalyserNode; // pre-gate: drives the legacy gate decision
  private readonly gate?: GainNode; // the legacy mute-between-words gain
  private readonly detectorBuf?: Float32Array<ArrayBuffer>;
  private gateTimer: ReturnType<typeof setInterval> | null = null;
  private gateOpen = false;
  /** Wall-clock (ms) until which the legacy gate is held open after the last over-threshold sample. */
  private holdUntil = 0;
  /** Wall-clock (ms) of the previous legacy detector tick, to detect a STARVED main-thread detector. */
  private lastTickWall = 0;
  private destroyed = false;

  private constructor(
    ctx: AudioContext,
    rawStream: MediaStream,
    vadNode: AudioWorkletNode | null,
    options?: Partial<MicPipelineOptions>,
  ) {
    this.ctx = ctx;
    this.opts = { ...DEFAULT_MIC_PIPELINE_OPTIONS, ...options };
    this.vadNode = vadNode;

    // ── Build the common front-end: source → HPF → LPF (+ self-meter tap) ────────────────────────
    this.source = ctx.createMediaStreamSource(rawStream);

    // High-pass: remove everything below speech (rumble/hum/thumps) — Q at the Butterworth default
    // (~0.707) for a flat, artifact-free roll-off.
    this.highPass = ctx.createBiquadFilter();
    this.highPass.type = 'highpass';
    this.highPass.frequency.value = this.opts.highPassHz;

    // Low-pass: gently shave the harshest high-frequency keystroke "tick" without dulling the voice.
    this.lowPass = ctx.createBiquadFilter();
    this.lowPass.type = 'lowpass';
    this.lowPass.frequency.value = this.opts.lowPassHz;

    // Self meter / VAD analyser → reads the FILTERED, PRE-gate signal (see the levelAnalyser doc).
    this.levelAnalyser = ctx.createAnalyser();
    this.levelAnalyser.fftSize = 512;

    this.destination = ctx.createMediaStreamDestination();

    this.source.connect(this.highPass);
    this.highPass.connect(this.lowPass);
    this.lowPass.connect(this.levelAnalyser); // self meter / ring (pre-gate, filtered) — both modes

    if (vadNode) {
      // ── PRIMARY: VAD worklet gate on the audio thread ──────────────────────────────────────────
      // The worklet gates on VOICED speech and outputs the gated signal; no main-thread detector/loop, so
      // it can never starve (the bug the legacy path works around). It posts {speaking} on each transition.
      this.lowPass.connect(vadNode);
      vadNode.connect(this.destination);
      vadNode.port.onmessage = (e: MessageEvent) => {
        const d = e.data as { speaking?: boolean; rms?: number; voicing?: number } | null;
        if (!d) return;
        this.speaking = Boolean(d.speaking);
        if (typeof d.rms === 'number') this.lastRms = d.rms;
        if (typeof d.voicing === 'number') this.lastVoicing = d.voicing;
      };
    } else {
      // ── FALLBACK: legacy main-thread RMS gate ──────────────────────────────────────────────────
      this.detectorAnalyser = ctx.createAnalyser();
      this.detectorAnalyser.fftSize = 512;
      this.detectorBuf = new Float32Array(new ArrayBuffer(this.detectorAnalyser.fftSize * 4));
      this.gate = ctx.createGain();
      this.gate.gain.value = 0; // start CLOSED so no room tone leaks before the first poll
      this.lowPass.connect(this.detectorAnalyser); // tap → gate decision
      this.lowPass.connect(this.gate); // the real signal path
      this.gate.connect(this.destination);
      this.gateTimer = setInterval(() => this.tickGate(), this.opts.pollMs);
    }

    const track = this.destination.stream.getAudioTracks()[0];
    if (!track) {
      // Should never happen (a destination always yields one track), but fail loud rather than ship a
      // silent producer — the caller falls back to the raw track.
      throw new Error('[mic-pipeline] MediaStreamAudioDestinationNode produced no audio track');
    }
    this.outputTrack = track;
  }

  /**
   * One LEGACY detector tick (fallback mode only): measure RMS of the filtered (pre-gate) signal and ramp
   * the gate gain open/shut with hysteresis + hold. Unused when the VAD worklet is active.
   */
  private tickGate(): void {
    if (this.destroyed || !this.detectorAnalyser || !this.detectorBuf || !this.gate) return;
    this.detectorAnalyser.getFloatTimeDomainData(this.detectorBuf);
    let sum = 0;
    for (let i = 0; i < this.detectorBuf.length; i++) {
      const s = this.detectorBuf[i]!;
      sum += s * s;
    }
    const rms = Math.sqrt(sum / this.detectorBuf.length);
    const now = this.ctx.currentTime;
    const wall = Date.now();
    // Gap since the last tick — used by decideGate to detect a STARVED detector (a main-thread stall under
    // render/sound load) and refuse to close the gate on that untrustworthy snapshot. 0 = first tick.
    const gap = this.lastTickWall === 0 ? 0 : wall - this.lastTickWall;
    this.lastTickWall = wall;

    const decision = decideGate({ open: this.gateOpen, holdUntil: this.holdUntil }, rms, wall, gap, this.opts);
    this.gateOpen = decision.open;
    this.holdUntil = decision.holdUntil;
    // Telemetry parity with the worklet path (ADR-0044 review): so __duckVoice().vad isn't dead in
    // fallback mode. (No voicing is computed by the legacy energy gate, so lastVoicing stays 0.)
    this.speaking = decision.open;
    this.lastRms = rms;
    if (decision.ramp === 'open') {
      // Speech onset → open fast (short constant) so the first consonant isn't clipped.
      this.gate.gain.setTargetAtTime(1, now, this.opts.attack);
    } else if (decision.ramp === 'close') {
      // Genuinely quiet past the hold (on a healthy tick) → release with the slow constant (smooth fade).
      this.gate.gain.setTargetAtTime(0, now, this.opts.release);
    }
  }

  /**
   * Re-apply filter corners / gate tunables live (e.g. a future settings panel). Cheap; no graph
   * rebuild. Unused thresholds simply take effect on the next detector tick.
   */
  setOptions(options: Partial<MicPipelineOptions>): void {
    Object.assign(this.opts, options);
    this.highPass.frequency.value = this.opts.highPassHz;
    this.lowPass.frequency.value = this.opts.lowPassHz;
  }

  /**
   * cost-census: tell the VAD worklet whether the mic is muted, so it skips the per-frame speech-DSP
   * (the `voicingScore` autocorrelation) entirely while the producer is paused — that work is pure waste,
   * and battery, when nothing is forwarded. No-op on the legacy RMS-gate fallback (`vadNode === null`),
   * where the muted track already reads as silence and the gate stays shut.
   */
  setMuted(muted: boolean): void {
    this.vadNode?.port.postMessage({ muted });
  }

  /**
   * Tear the whole chain down deterministically — stop the detector loop, disconnect every node, and
   * stop the produced output track. Idempotent. Called on mute-independent lifecycle events: stop,
   * recoverable failStart, reconnect re-handshake, and device hot-swap, so NO AudioContext nodes or
   * tracks leak across a re-handshake (M3.5b).
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.gateTimer) clearInterval(this.gateTimer);
    this.gateTimer = null;
    this.lastTickWall = 0; // a fresh pipeline (re-handshake) must not treat its first tick as a stall
    if (this.vadNode) this.vadNode.port.onmessage = null; // drop the worklet message handler
    for (const node of [
      this.source,
      this.highPass,
      this.lowPass,
      this.detectorAnalyser,
      this.gate,
      this.vadNode,
      this.levelAnalyser,
      this.destination,
    ]) {
      try {
        node?.disconnect();
      } catch {
        /* a node may already be detached, or absent in the other gate mode */
      }
    }
    try {
      this.outputTrack.stop();
    } catch {
      /* already stopped */
    }
  }
}
